import React from "react";
import {
  ArrowLeft,
  Smartphone,
  Apple,
  Cuboid as Android,
  Zap,
  Shield,
  Users,
  Code,
  CheckCircle,
  Star,
  ArrowRight,
} from "lucide-react";

interface MobileAppsPageProps {
  onBack: () => void;
}

const MobileAppsPage: React.FC<MobileAppsPageProps> = ({ onBack }) => {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const features = [
    {
      icon: Zap,
      title: "Rendimiento Optimizado",
      description:
        "Aplicaciones rápidas y eficientes que ofrecen una experiencia fluida en cualquier dispositivo.",
    },
    {
      icon: Shield,
      title: "Seguridad Avanzada",
      description:
        "Implementamos las mejores prácticas de seguridad para proteger los datos de tus usuarios.",
    },
    {
      icon: Users,
      title: "UX/UI Intuitivo",
      description:
        "Diseños centrados en el usuario que garantizan una experiencia excepcional y fácil de usar.",
    },
    {
      icon: Code,
      title: "Código Limpio",
      description:
        "Desarrollo con estándares de calidad que facilitan el mantenimiento y futuras actualizaciones.",
    },
  ];

  const technologies = [
    {
      name: "React Native",
      description: "Desarrollo multiplataforma con código compartido",
      logo: "⚛️",
    },
  ];

  const process = [
    {
      step: "01",
      title: "Análisis y Planificación",
      description:
        "Definimos objetivos, audiencia objetivo y funcionalidades clave de tu aplicación móvil.",
    },
    {
      step: "02",
      title: "Diseño UX/UI",
      description:
        "Creamos wireframes y prototipos interactivos para validar la experiencia de usuario.",
    },
    {
      step: "03",
      title: "Desarrollo",
      description:
        "Programamos tu app usando las mejores tecnologías y prácticas de desarrollo móvil.",
    },
    {
      step: "04",
      title: "Testing y QA",
      description:
        "Realizamos pruebas exhaustivas en múltiples dispositivos para garantizar calidad.",
    },
    {
      step: "05",
      title: "Lanzamiento",
      description:
        "Publicamos tu app en las tiendas oficiales y proporcionamos soporte post-lanzamiento.",
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 transition-colors duration-200"
            >
              <ArrowLeft className="h-5 w-5" />
              <span>Volver a Servicios</span>
            </button>
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-1">
                <img
                  src="/src/assets/logoModelos2.png"
                  alt="ModeloSmart Logo"
                  style={{ height: "120px", width: "auto" }}
                />
              </div>{" "}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 via-white to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-6">
                <div className="flex items-center space-x-3">
                  <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center">
                    <Smartphone className="h-8 w-8 text-blue-600" />
                  </div>
                  <h1 className="text-5xl font-bold text-gray-900">
                    Aplicaciones <span className="text-blue-600">Móviles</span>
                  </h1>
                </div>
                <p className="text-xl text-gray-600 leading-relaxed">
                  Desarrollamos aplicaciones móviles nativas y multiplataforma
                  que conectan tu negocio con tus clientes de manera directa,
                  eficiente y memorable.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => scrollToSection("contacto")}
                  className="inline-flex items-center px-8 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-all duration-200 transform hover:scale-105 shadow-lg"
                >
                  Solicitar Cotización
                  <ArrowRight className="ml-2 h-5 w-5" />
                </button>

              </div>

              {/* Platform Icons */}
              <div className="flex items-center space-x-6 pt-4">
                <div className="flex items-center space-x-2">
                  <Apple className="h-8 w-8 text-gray-700" />
                  <span className="text-gray-600 font-medium">iOS</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Android className="h-8 w-8 text-green-600" />
                  <span className="text-gray-600 font-medium">Android</span>
                </div>
              </div>
            </div>

            {/* Hero Image */}
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/607812/pexels-photo-607812.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Desarrollo de aplicaciones móviles"
                className="w-full h-96 object-cover rounded-2xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/20 to-transparent rounded-2xl"></div>

              {/* Floating elements */}
              <div className="absolute -top-6 -right-6 bg-white p-4 rounded-xl shadow-lg">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-sm font-medium">
                    Apps en desarrollo
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              ¿Por qué elegir nuestras{" "}
              <span className="text-blue-600">aplicaciones móviles</span>?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Combinamos experiencia técnica con diseño centrado en el usuario
              para crear aplicaciones que realmente marquen la diferencia.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-gray-50 p-8 rounded-2xl hover:shadow-lg transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                  <feature.icon className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {feature.title}
                </h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Tecnologías que <span className="text-blue-600">Dominamos</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Utilizamos las mejores herramientas y frameworks para crear
              aplicaciones móviles de alta calidad y rendimiento.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {technologies.map((tech, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 text-center"
              >
                <div className="text-4xl mb-4">{tech.logo}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {tech.name}
                </h3>
                <p className="text-gray-600">{tech.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Nuestro <span className="text-blue-600">Proceso</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Seguimos una metodología probada que garantiza el éxito de tu
              aplicación móvil desde la conceptualización hasta el lanzamiento.
            </p>
          </div>

          <div className="relative">
            {/* Process line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-600 to-teal-600 hidden lg:block"></div>

            <div className="space-y-12">
              {process.map((step, index) => (
                <div key={index} className="relative flex items-start">
                  <div className="hidden lg:flex w-16 h-16 bg-blue-600 rounded-full items-center justify-center text-white font-bold text-lg mr-8 flex-shrink-0 shadow-lg">
                    {step.step}
                  </div>

                  <div className="bg-white p-8 rounded-2xl flex-1 shadow-lg hover:shadow-xl transition-shadow duration-300">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 text-lg">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        id="contacto"
        className="py-20 bg-gradient-to-br from-blue-900 via-blue-800 to-teal-700"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="space-y-8">
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              ¿Listo para crear tu{" "}
              <span className="text-yellow-400">aplicación móvil</span>?
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Convierte tu idea en una aplicación móvil exitosa. Nuestro equipo
              está listo para hacer realidad tu visión.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <button
                onClick={onBack}
                className="inline-flex items-center px-8 py-4 bg-white text-blue-900 font-bold text-lg rounded-xl hover:bg-gray-100 transition-all duration-200 transform hover:scale-105 shadow-2xl"
              >
                Solicitar Cotización
                <ArrowRight className="ml-3 h-6 w-6" />
              </button>
              <button
                onClick={onBack}
                className="inline-flex items-center px-8 py-4 border-2 border-white/50 text-white font-semibold text-lg rounded-xl hover:bg-white/10 hover:border-white transition-all duration-200"
              >
                Hablar con un Experto
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MobileAppsPage;
